import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
const ChatWidget = ({ initialAgent, demo = false, demoStep = 0, inModal = false }) => {
    // --- Static Data (kept as is) ---
    const demoMessages = [
        { type: 'user', text: 'Hi, I need help with my order' },
        { type: 'ai', text: 'Hello! I\'d be happy to help you with your order. Could you please provide your order number?' },
        { type: 'user', text: 'It\'s #12345' },
        { type: 'ai', text: 'Thank you! I can see your order #12345 for the premium subscription. It looks like there was an issue with the payment processing. Let me help you resolve this right away.' },
        { type: 'user', text: 'What\'s the status?' },
        { type: 'ai', text: 'Your order is currently pending payment verification. I\'ve just processed a manual approval for you. You should receive a confirmation email within the next 5 minutes. Is there anything else I can assist you with today?' }
    ];
    const agentMap = {
        'customer-support': 'agent_6001k58zst6mf9682r3prf8ypams',
        'sales-conversations': 'agent_5201k592gazkemrtgzr07x37gxv3',
        'voice-assistants': 'agent_4201k59473txe3a89qp91tj87m6w',
        'hr-conversations': 'agent_2601k5ax59v5fbz939pcd3d44wtq',
        'chat-support': 'agent_0901k594pp28es8sh0p2e7m80d21',
        default: 'agent_0901k594pp28es8sh0p2e7m80d21'
    };
    // --- State and Refs ---
    const [agentType, setAgentType] = useState(initialAgent || localStorage.getItem('selectedAIAgent') || 'chat-support');
    const [isElementReady, setIsElementReady] = useState(false);
    // Ref for the elevenlabs-convai element (must be attached in the return/render section)
    const widgetRef = useRef(null); // Use 'any' for the custom element
    // Load user data for 'user-id'
    const userEmail = localStorage.getItem("userEmail");
    const userPhone = localStorage.getItem("userPhone");
    const userId = userEmail && userPhone ? `${userEmail} (${userPhone})` : userEmail || userPhone || "guest_user";
    const resolvedAgentId = agentMap[agentType] || agentMap.default;
    // --- Lifecycle Hooks (Existing Logic) ---
    // 1. Agent change logic (kept as is)
    const agentTypeRef = useRef(agentType);
    useEffect(() => { agentTypeRef.current = agentType; }, [agentType]);
    useEffect(() => {
        const handleAgentChanged = () => {
            const selected = localStorage.getItem('selectedAIAgent');
            if (selected && selected !== agentTypeRef.current) {
                setAgentType(selected);
            }
        };
        window.addEventListener('agentChanged', handleAgentChanged);
        return () => { window.removeEventListener('agentChanged', handleAgentChanged); };
    }, []);
    // 2. Script loading (kept as is)
    useEffect(() => {
        const existingScript = document.querySelector('script[src="https://unpkg.com/@elevenlabs/convai-widget-embed"]');
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
            script.async = true;
            script.type = 'text/javascript';
            document.head.appendChild(script);
        }
    }, []);
    // 3. Custom element polling (kept as is)
    useEffect(() => {
        let pollInterval;
        let attempts = 0;
        const maxAttempts = 20;
        const checkCustomElement = () => {
            attempts++;
            if (typeof customElements !== 'undefined' && customElements.get('elevenlabs-convai')) {
                setIsElementReady(true);
                clearInterval(pollInterval);
            }
            else if (attempts >= maxAttempts) {
                setIsElementReady(true);
                clearInterval(pollInterval);
            }
        };
        pollInterval = setInterval(checkCustomElement, 500);
        checkCustomElement();
        return () => { clearInterval(pollInterval); };
    }, []);
    // --- NEW: Conversation ID Poller & Hiding Logic ---
    useEffect(() => {
        if (!isElementReady)
            return;
        const widget = widgetRef.current || document.querySelector("elevenlabs-convai");
        if (!widget)
            return;
        let conversationIdFound = localStorage.getItem('elevenlabsConversationId') !== null;
        let pollInterval = null;
        // Function to check internal properties for the ID
        const pollForConversationId = () => {
            if (conversationIdFound) {
                clearInterval(pollInterval);
                return true;
            }
            // Attempt to access non-public properties set on the JS object
            const possibleProperties = [
                widget.conversationId,
                widget.convaiId,
                widget.getAttribute('data-conversation-id'),
                widget.getAttribute('convai-id'),
            ];
            // Look for a string ID that is long enough to be an actual conversation ID
            const foundId = possibleProperties.find(id => id && typeof id === 'string' && id.length > 10);
            if (foundId) {
                console.log(`🚀 CONVERSATION ID FOUND via Polling/Property Access: ${foundId}`);
                localStorage.setItem('elevenlabsConversationId', foundId);
                // --- ACTION: SEND EMAIL HERE ---
                if (userEmail) {
                    console.log(`✉️ Triggering API to send ID ${foundId} to ${userEmail}`);
                    // Example API call:
                    // fetch('/api/send-convai-id', { 
                    //   method: 'POST', 
                    //   body: JSON.stringify({ email: userEmail, conversationId: foundId })
                    // });
                }
                // -----------------------------
                conversationIdFound = true;
                clearInterval(pollInterval);
                return true;
            }
            return false;
        };
        // Function to hide "Powered by" text (inside Shadow DOM)
        const checkAndHide = () => {
            if (widget.shadowRoot) {
                const elements = widget.shadowRoot.querySelectorAll('*');
                elements.forEach((el) => {
                    if (el.textContent && el.textContent.includes("Powered by")) {
                        el.style.display = "none";
                    }
                });
            }
        };
        // Setup Mutation Observer for hiding
        let hideObserver = null;
        if (widget.shadowRoot) {
            hideObserver = new MutationObserver(checkAndHide);
            hideObserver.observe(widget.shadowRoot, { childList: true, subtree: true });
        }
        // Start Polling for ID every 1000ms
        pollInterval = setInterval(pollForConversationId, 1000);
        pollForConversationId(); // Initial check
        // Cleanup
        return () => {
            if (pollInterval)
                clearInterval(pollInterval);
            if (hideObserver)
                hideObserver.disconnect();
        };
    }, [isElementReady, resolvedAgentId, userEmail]);
    // --- Render (Return) ---
    if (demo) {
        // Demo mode rendering (kept as is)
        return (_jsx("div", { className: "bg-sand rounded-lg p-4 max-h-96 overflow-y-auto", children: _jsxs("div", { className: "space-y-4", children: [demoMessages.slice(0, demoStep).map((message, index) => (_jsx("div", { className: `flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`, children: _jsx("div", { className: `max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.type === 'user'
                                ? 'bg-rust text-cream'
                                : 'bg-white text-bark border border-[rgba(16,35,62,0.12)]'}`, children: _jsx("p", { className: "text-sm", children: message.text }) }) }, index))), demoStep <= demoMessages.length && (_jsx("div", { className: "flex justify-start", children: _jsx("div", { className: "bg-white text-bark border border-[rgba(16,35,62,0.12)] px-4 py-2 rounded-lg max-w-xs lg:max-w-md", children: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("div", { className: "w-2 h-2 bg-gray-400 rounded-full animate-bounce" }), _jsx("div", { className: "w-2 h-2 bg-gray-400 rounded-full animate-bounce", style: { animationDelay: '0.1s' } }), _jsx("div", { className: "w-2 h-2 bg-gray-400 rounded-full animate-bounce", style: { animationDelay: '0.2s' } }), _jsx("span", { className: "text-sm text-gray-500", children: "AI is typing..." })] }) }) }))] }) }));
    }
    return (_jsx(_Fragment, { children: !inModal ? (_jsx("div", { id: "elevenlabs-widget", style: {
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 9999,
                width: '277px',
                height: '120px'
            }, children: isElementReady && (_jsx("elevenlabs-convai", { ref: widgetRef, "agent-id": resolvedAgentId, "user-id": userId }, resolvedAgentId)) })) : (_jsx("div", { id: "elevenlabs-modal-widget", className: "modal-widget", children: isElementReady && (_jsx("elevenlabs-convai", { ref: widgetRef, "agent-id": resolvedAgentId, "user-id": userId }, resolvedAgentId)) })) }));
};
export default ChatWidget;
